<template>
  <Line :id="chartId" :data="chartData" :options="chartOptions" />
</template>

<script setup>
import { computed, watch } from 'vue';
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Tooltip,
  CategoryScale,
  Title,
  Legend
} from 'chart.js';
import { Line } from 'vue-chartjs';
import { isPlainObject } from 'lodash-es';

ChartJS.register(LineController, LineElement, PointElement, LinearScale, Title, Tooltip, CategoryScale, Legend);

// --- Helper Functions ---
const formatTimestamp = (ts) => {
  if (typeof ts !== 'string') return [ts];
  // Splits "2026-06-04T16:50:57" into ["2026-06-04", "16:50:57 UTC"]
  const tsParts = [
    ts.slice(0, 10),
    ts.slice(11, 19) + (ts.endsWith('Z') ? ' UTC' : ''),
  ];
  return tsParts;
};

// --- Colors for Multi-Device Overlays ---
const lineColors = [
  '#2563eb', '#dc2626', '#16a34a', '#ca8a04', 
  '#9333ea', '#0891b2', '#ea580c', '#4b5563'
];

// --- Props & Validators ---
const props = defineProps({
  measType: {   // The measurement type used as the graph title
    type: String,
    required: true,
  },
  units: {   // The measurement units
    type: String,
    required: true,
  },
  lineLabel: {   // The entity label for overlaid lines (used in single-array mode)
    type: String,
  },
  measurements: {
    type: [Array, Object],
    required: true,
    validator(value, typeProps) {
      const tags = ['measurementTime', typeProps.measType];
      
      // Validate flat Array format
      if (Array.isArray(value)) {
        return value.every(meas => 
          isPlainObject(meas) && tags.every(tag => Object.hasOwn(meas, tag))
        );
      }
      
      // Validate Object format { deviceId: telemetry[] }
      if (isPlainObject(value)) {
        return Object.values(value).every(arr => {
          return (
            Array.isArray(arr) && 
            arr.every(meas => isPlainObject(meas) && 
            tags.every(tag => Object.hasOwn(meas, tag)))
          );
        });
      }
      return false;
    }
  }
});

// --- Dynamic Chart Configuration ---
const chartId = computed(() => `${props.measType}Chart`);

const chartConfig = computed(() => {
  const timeLabelsSet = new Set();
  const rawDatasets = new Map(); // Keyed by line descriptor label
  const isMultiDeviceMode = !Array.isArray(props.measurements);

  // Helper logic to parse points into sets
  const processPoint = (point, label) => {
    if (!point || !Object.hasOwn(point, props.measType)) return;
    // Format timestamps for readability
    const formattedLabelArray = formatTimestamp(point.measurementTime);
    // Convert to a flat tracking string string for object key isolation
    const mapKey = formattedLabelArray.join(' '); 
    // Add the structural array directly to the ChartJS Axis tracking Set
    timeLabelsSet.add(formattedLabelArray);
    if (!rawDatasets.has(label)) {
      rawDatasets.set(label, { label, tempData: {} });
    }
    rawDatasets.get(label).tempData[mapKey] = point[props.measType];
  };

  // Process data from properties cleanly based on mode
  if (isMultiDeviceMode) {
    Object.entries(props.measurements).forEach(([deviceId, telemetryArray]) => {
      telemetryArray.forEach(point => processPoint(point, deviceId));
    });
  } else {
    props.measurements.forEach(point => processPoint(point, props.lineLabel));
  }

  // Align chronological timeline axis array
  const sortedTimeline = Array.from(timeLabelsSet).sort((a, b) => {
    const timeA = Array.isArray(a) ? a.join(' ') : String(a);
    const timeB = Array.isArray(b) ? b.join(' ') : String(b);
    return new Date(timeA) - new Date(timeB);
  });
  // const sortedTimeline = Array.from(timeLabelsSet).sort((a, b) => new Date(a) - new Date(b));

  // Align values array with null fill-ins 
  let colorIndex = 0;
  const synchronizedDatasets = Array.from(rawDatasets.values()).map((ds) => {
    // Map using the joined lookup string key
    const alignedValues = sortedTimeline.map(timeArray => {
      const mapKey = Array.isArray(timeArray) 
        ? timeArray.join(' ') 
        : String(timeArray);
      return ds.tempData[mapKey] ?? null;
    });
    const datasetColor = lineColors[colorIndex % lineColors.length];
    colorIndex++;

    return {
      type: 'line',
      label: ds.label,
      data: alignedValues,
      tension: 0.4,
      spanGaps: true,
      borderColor: datasetColor,
      backgroundColor: datasetColor,
    };
  });

  return {
    labels: sortedTimeline,
    datasets: synchronizedDatasets
  };
});

// --- Exposed Computeds for vue-chartjs wrapper ---
const chartData = computed(() => chartConfig.value);

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      title: { display: false, text: 'Time' },
      ticks: { maxRotation: 0, minRotation: 0 },
    },
    y: {
      title: { display: true, text: props.units }
    }
  },
  plugins: {
    title: {
      display: true,
      text: props.measType.toUpperCase(),
    },
    // Show legend label tags when dealing with multiple device tracks overlaid together
    legend: { display: !Array.isArray(props.measurements) || !!props.lineLabel },
  }
}));
</script>

<style scoped>
</style>
